import {
  Alert,
  App,
  Button,
  Card,
  Empty,
  Form,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { feeApi, type FeeFrequency, type FeeStructure } from '@/api/fee';
import { setupApi } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

const FREQUENCY_LABEL: Record<FeeFrequency, string> = {
  MONTHLY: 'Monthly',
  ONE_TIME: 'One-time',
  PER_TERM: 'Per term',
};
const FREQUENCY_COLOR: Record<FeeFrequency, string> = {
  MONTHLY: 'processing',
  ONE_TIME: 'success',
  PER_TERM: 'warning',
};

interface StructureFormShape {
  feeTypeId: number;
  amount: number;
}

export function FeeStructuresPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const academicYearId = useAuthStore((s) => s.academicYearId);
  const canCreate = useAuthStore((s) => s.hasAuthority('FEE_MGMT:CREATE'));
  const canDelete = useAuthStore((s) => s.hasAuthority('FEE_MGMT:DELETE'));

  const [classSectionId, setClassSectionId] = useState<number | undefined>();
  const [editing, setEditing] = useState<FeeStructure | null>(null);
  const [creating, setCreating] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [form] = Form.useForm<StructureFormShape>();

  const { data: classSections } = useQuery({
    queryKey: ['setup', 'class-sections', academicYearId ?? 'none'],
    queryFn: () => setupApi.listClassSections(academicYearId ?? undefined),
    enabled: academicYearId != null,
    staleTime: 5 * 60 * 1000,
  });

  const { data: feeTypes } = useQuery({
    queryKey: ['fee', 'types', 'active'],
    queryFn: () => feeApi.listTypes(true),
    staleTime: 5 * 60 * 1000,
  });

  const structuresQuery = useQuery({
    queryKey: ['fee', 'structures', academicYearId, classSectionId],
    queryFn: () => feeApi.listStructures(academicYearId!, classSectionId),
    enabled: academicYearId != null && classSectionId != null,
  });
  const rows = structuresQuery.data ?? [];

  const upsert = useMutation({
    mutationFn: (values: StructureFormShape) =>
      feeApi.upsertStructure({
        academicYearId: academicYearId!,
        classSectionId: classSectionId!,
        feeTypeId: values.feeTypeId,
        amount: values.amount.toFixed(2),
      }),
    onSuccess: () => {
      message.success(editing ? 'Amount updated' : 'Fee added to structure');
      qc.invalidateQueries({ queryKey: ['fee', 'structures'] });
      setEditing(null);
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const remove = useMutation({
    mutationFn: (id: number) => feeApi.deleteStructure(id),
    onSuccess: () => {
      message.success('Fee removed from structure');
      qc.invalidateQueries({ queryKey: ['fee', 'structures'] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const copy = useMutation({
    mutationFn: async (sourceCs: number) => {
      const src = await feeApi.listStructures(academicYearId!, sourceCs);
      let copied = 0;
      const usedByThisCs = new Set(rows.map((r) => r.feeTypeId));
      for (const row of src) {
        if (usedByThisCs.has(row.feeTypeId)) continue;
        await feeApi.upsertStructure({
          academicYearId: academicYearId!,
          classSectionId: classSectionId!,
          feeTypeId: row.feeTypeId,
          amount: row.amount,
        });
        copied++;
      }
      return copied;
    },
    onSuccess: (copied) => {
      message.success(`Copied ${copied} fee row${copied === 1 ? '' : 's'}`);
      qc.invalidateQueries({ queryKey: ['fee', 'structures'] });
      setCopyOpen(false);
    },
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    form.resetFields();
  };
  const openEdit = (row: FeeStructure) => {
    setCreating(false);
    setEditing(row);
    form.setFieldsValue({
      feeTypeId: row.feeTypeId,
      amount: Number(row.amount),
    });
  };

  const open = creating || editing != null;
  const usedFeeTypeIds = new Set(rows.map((r) => r.feeTypeId));
  const feeTypeOptions = (feeTypes ?? []).map((t) => ({
    value: t.id,
    label: `${t.name} · ${FREQUENCY_LABEL[t.frequency]}`,
    disabled: editing == null && usedFeeTypeIds.has(t.id),
  }));

  const columns: TableProps<FeeStructure>['columns'] = [
    {
      title: 'Fee type',
      dataIndex: 'feeTypeName',
      render: (n: string) => <span style={{ fontWeight: 600 }}>{n}</span>,
    },
    {
      title: 'Frequency',
      dataIndex: 'frequency',
      width: 140,
      render: (f: FeeFrequency | null) =>
        f ? <Tag color={FREQUENCY_COLOR[f]}>{FREQUENCY_LABEL[f]}</Tag> : '—',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      width: 160,
      align: 'right',
      render: (a: string) => <Typography.Text strong>{Number(a).toLocaleString('en-PK')}</Typography.Text>,
    },
    {
      title: 'Actions',
      width: 130,
      align: 'right',
      render: (_, row) => (
        <Space>
          {canCreate && (
            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)}>
              Edit
            </Button>
          )}
          {canDelete && (
            <Popconfirm
              title="Remove from structure?"
              okText="Remove"
              okButtonProps={{ danger: true }}
              onConfirm={() => remove.mutate(row.id)}
            >
              <Button size="small" danger type="text" icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const selectedCs = classSections?.find((cs) => cs.id === classSectionId);

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Fee"
        title={
          selectedCs
            ? `Fee structures · ${selectedCs.className} · ${selectedCs.sectionName}`
            : 'Fee structures'
        }
        description="The amounts charged for each fee type per class·section. Generation is done from the Generate page once the structure is set."
        count={
          selectedCs && rows.length > 0 ? { value: rows.length, label: 'fee rows' } : undefined
        }
        actions={
          <Space>
            {canCreate && (
              <Button
                icon={<CopyOutlined />}
                disabled={classSectionId == null}
                onClick={() => setCopyOpen(true)}
              >
                Copy from…
              </Button>
            )}
            {canCreate && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                disabled={classSectionId == null || (feeTypes ?? []).length === 0}
                onClick={openCreate}
              >
                Add fee
              </Button>
            )}
          </Space>
        }
      />

      <Space wrap size={12}>
        <Select
          placeholder="Class · Section"
          value={classSectionId}
          onChange={setClassSectionId}
          options={(classSections ?? [])
            .slice()
            .sort((a, b) => a.classSortOrder - b.classSortOrder || a.sectionName.localeCompare(b.sectionName))
            .map((cs) => ({ value: cs.id, label: `${cs.className} · ${cs.sectionName}` }))}
          style={{ minWidth: 240 }}
          showSearch
          optionFilterProp="label"
        />
      </Space>

      {classSectionId == null ? (
        <Empty description="Pick a class · section to manage its fee structure" />
      ) : (
        <Card styles={{ body: { padding: 0 } }}>
          <Table
            rowKey="id"
            size="middle"
            loading={structuresQuery.isFetching}
            columns={columns}
            dataSource={rows}
            pagination={false}
            locale={{
              emptyText: (
                <Empty description={`No fees defined for ${selectedCs?.className ?? ''} ${selectedCs?.sectionName ?? ''} yet`}>
                  {canCreate && (
                    <Space>
                      <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                        Add first fee
                      </Button>
                      <Button icon={<CopyOutlined />} onClick={() => setCopyOpen(true)}>
                        Copy from another class
                      </Button>
                    </Space>
                  )}
                </Empty>
              ),
            }}
          />
        </Card>
      )}

      {/* Add/edit modal */}
      <Modal
        title={editing ? `Edit ${editing.feeTypeName}` : 'Add fee'}
        open={open}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        onOk={() => form.submit()}
        confirmLoading={upsert.isPending}
        okText={editing ? 'Save' : 'Add'}
        destroyOnHidden
      >
        {open && (
          <Form form={form} layout="vertical" onFinish={(values) => upsert.mutate(values)}>
            <Form.Item label="Fee type" name="feeTypeId" rules={[{ required: true }]}>
              <Select disabled={editing != null} showSearch optionFilterProp="label" options={feeTypeOptions} />
            </Form.Item>
            <Form.Item
              label="Amount (PKR)"
              name="amount"
              rules={[{ required: true, type: 'number', min: 0 }]}
            >
              <InputNumber style={{ width: '100%' }} step={100} min={0} />
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Copy from another class·section */}
      <CopyFromModal
        open={copyOpen}
        currentCs={classSectionId}
        academicYearId={academicYearId}
        classSections={(classSections ?? []).filter((cs) => cs.id !== classSectionId)}
        onCancel={() => setCopyOpen(false)}
        onConfirm={(sourceCs) => copy.mutate(sourceCs)}
        submitting={copy.isPending}
      />
    </div>
  );
}

interface ClassSectionLite {
  id: number;
  className: string;
  sectionName: string;
  classSortOrder: number;
}

function CopyFromModal({
  open,
  currentCs,
  academicYearId,
  classSections,
  onCancel,
  onConfirm,
  submitting,
}: {
  open: boolean;
  currentCs: number | undefined;
  academicYearId: number | null;
  classSections: ClassSectionLite[];
  onCancel: () => void;
  onConfirm: (sourceCs: number) => void;
  submitting: boolean;
}) {
  const [sourceCs, setSourceCs] = useState<number | undefined>();
  const preview = useQuery({
    queryKey: ['fee', 'structures', 'copy-preview', academicYearId, sourceCs],
    queryFn: () => feeApi.listStructures(academicYearId!, sourceCs!),
    enabled: open && academicYearId != null && sourceCs != null,
    staleTime: 60 * 1000,
  });

  return (
    <Modal
      open={open}
      title="Copy fees from another class · section"
      onCancel={() => {
        setSourceCs(undefined);
        onCancel();
      }}
      onOk={() => sourceCs != null && onConfirm(sourceCs)}
      okText={preview.data ? `Copy ${preview.data.length} row${preview.data.length === 1 ? '' : 's'}` : 'Copy'}
      okButtonProps={{ disabled: sourceCs == null || !preview.data || preview.data.length === 0 }}
      confirmLoading={submitting}
      destroyOnHidden
    >
      <Form layout="vertical">
        <Form.Item label="Source class · section">
          <Select
            placeholder="Pick the class · section to copy from"
            value={sourceCs}
            onChange={setSourceCs}
            disabled={currentCs == null}
            options={classSections
              .slice()
              .sort((a, b) => a.classSortOrder - b.classSortOrder || a.sectionName.localeCompare(b.sectionName))
              .map((cs) => ({ value: cs.id, label: `${cs.className} · ${cs.sectionName}` }))}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>
      </Form>
      {sourceCs != null && (
        <Alert
          type="info"
          showIcon
          title={
            preview.isFetching
              ? 'Loading…'
              : preview.data && preview.data.length > 0
                ? `Will copy ${preview.data.length} fee row(s). Fee types already in the target class are skipped.`
                : 'Source has no fees defined.'
          }
        />
      )}
    </Modal>
  );
}

